import type { StoredSession, TokenStore } from "../../api/session";

/**
 * A Helmr support session (console-support) reaches this app as `/support#t=<token>&b=<businessId>&s=<sessionId>&e=<endsAtMs>`,
 * opened by the console in a new tab. The token travels in the fragment so no server ever sees it in a request line or log.
 * It is moved at once into this tab's sessionStorage, never localStorage: it dies with the tab and can never replace or
 * outlive a real staff login, and the fragment is scrubbed from the address bar and history.
 */
const KEY = "helmr.support.session";
const TOKEN_RE = /^[\x21-\x7e]{16,512}$/;
const BUSINESS_RE = /^biz_[A-Za-z0-9_]{1,64}$/;

interface Stored { token: string; expiresAt: number; businessId: string }

function parseFragment(hash: string, now: number): Stored | null {
  const p = new URLSearchParams(hash.replace(/^#/, ""));
  const token = p.get("t") ?? "";
  const businessId = p.get("b") ?? "";
  const expiresAt = Number(p.get("e"));
  if (!TOKEN_RE.test(token) || !BUSINESS_RE.test(businessId) || !Number.isFinite(expiresAt) || expiresAt <= now) return null;
  return { token, businessId, expiresAt };
}

function readStored(storage: Storage, now: number): Stored | null {
  try {
    const raw = storage.getItem(KEY);
    const v = raw ? (JSON.parse(raw) as Partial<Stored>) : null;
    if (v && typeof v.token === "string" && typeof v.businessId === "string" && typeof v.expiresAt === "number" && v.expiresAt > now) return v as Stored;
  } catch { /* unreadable: no support session */ }
  storage.removeItem(KEY);
  return null;
}

/** The token store for a support tab, or null when this tab is not one (the app then uses its normal store). */
export function supportTokenStore(
  env: { pathname: string; hash: string; now: number; storage: Storage; scrub: () => void } = {
    pathname: location.pathname, hash: location.hash, now: Date.now(), storage: globalThis.sessionStorage, scrub: () => history.replaceState(null, "", "/home"),
  },
): TokenStore | null {
  if (env.pathname === "/support") {
    const fresh = parseFragment(env.hash, env.now);
    env.scrub(); // whether or not it parsed, the fragment does not stay in the address bar
    if (fresh) env.storage.setItem(KEY, JSON.stringify(fresh));
  }
  const kept = readStored(env.storage, env.now);
  if (!kept) return null;
  const session: StoredSession = { token: kept.token, expiresAt: kept.expiresAt, stage: "scoped", businessId: kept.businessId };
  let current: StoredSession | null = session;
  return {
    read: () => current,
    write(v) {
      current = v;
      if (v === null) env.storage.removeItem(KEY);
    },
    deviceId: () => "support",
  };
}
